import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LuArrowRight,
  LuDroplets,
  LuPackage,
  LuShoppingBag,
} from 'react-icons/lu';
import { apiFetch, apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { recipeDefaultsForBrickSize } from '../constants/factoryBrickRecipeDefaults';
import {
  formatSandStockAvailability,
  formatSandUsageLine,
  stockQuantityToTonnes,
  tonnesPerLorry,
} from '../constants/sandLorryCapacity';
import { formatSandAmount } from '../utils/brickSandUnits';
import {
  resolveMaterialProduct,
  validateSandTonnesUsage,
  validateStockUsage,
} from '../utils/brickMaterialStock';
import {
  calcExpectedBricksFromCementBags,
  calcSandPer1000FromPerBag,
  calcSandPerCementBagFromPer1000,
  calcSandTonnesFromCementBags,
  effectiveSandPerCementBag,
  formatBrickVariance,
} from '../utils/brickProductionSand';
import { normalizeRole } from '../utils/userLocale';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import { tenantDisplayName } from '../utils/businessType';
import { brandingFromUser } from '../utils/tenantBranding';
import BrickSizeField from '../components/BrickSizeField';
import './BrickFactoryPage.css';

const PRODUCTION_EXPORT_HEAD = [
  'Date',
  'Brick type',
  'Good bricks (pcs)',
  'Ready (pcs)',
  'Curing (pcs)',
  'Cement bags',
  'Sand',
  'Recorded by',
];

function productionExportRow(b) {
  return [
    b.productionDate ?? '',
    b.brickTypeName ?? '',
    b.netPieces ?? b.quantityProduced ?? '',
    b.quantityToReady ?? '',
    b.quantityToCuring ?? '',
    b.cementBags ?? '',
    formatSandAmount(b.sandQuantity, b.sandUnit) || '',
    b.recordedBy ?? '',
  ];
}

function isDamageBatch(b) {
  return b.recordType === 'damage';
}

function formatPcs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-TZ');
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTzs(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-TZ', { maximumFractionDigits: 0 });
}

function normalizeBrickTypeKey(value) {
  return String(value || '').trim().toLowerCase();
}

function findDuplicateBrickType(types, { name, code, sizeLabel }) {
  const normName = normalizeBrickTypeKey(name);
  const normCode = normalizeBrickTypeKey(code);
  const normSize = normalizeBrickTypeKey(sizeLabel);

  if (normName) {
    const byName = types.find((t) => normalizeBrickTypeKey(t.name) === normName);
    if (byName) return byName;
  }
  if (normCode) {
    const byCode = types.find((t) => normalizeBrickTypeKey(t.code) === normCode);
    if (byCode) return byCode;
  }
  if (normSize) {
    const bySize = types.find((t) => normalizeBrickTypeKey(t.sizeLabel) === normSize);
    if (bySize) return bySize;
  }
  return null;
}

function typeToEditForm(type) {
  const sandPerBag =
    type.sandPerCementBag != null && Number(type.sandPerCementBag) > 0
      ? String(type.sandPerCementBag)
      : calcSandPerCementBagFromPer1000(type.sandPer1000Bricks, type.bricksPerCementBag) || '';
  return {
    name: type.name || '',
    code: type.code || '',
    sizeLabel: type.sizeLabel || '',
    defaultSellingPrice:
      type.defaultSellingPrice != null ? String(type.defaultSellingPrice) : '',
    bricksPerCementBag:
      type.bricksPerCementBag != null ? String(type.bricksPerCementBag) : '',
    sandPerCementBag: sandPerBag,
    sandPer1000Bricks:
      type.sandPer1000Bricks != null ? String(type.sandPer1000Bricks) : '',
    sandUnit: type.sandUnit || 'tonne',
  };
}

function formatCatalogDate(type) {
  const created = type?.createdAt;
  const updated = type?.updatedAt;
  if (!created) return '—';
  const wasUpdated = updated && updated !== created;
  const d = new Date(wasUpdated ? updated : created);
  if (Number.isNaN(d.getTime())) return '—';
  const label = wasUpdated ? 'Updated' : 'Added';
  return `${label} ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** SKU-style code from display name, e.g. "Solid brick 5 inch" → "SOLID-BRICK-5-INCH". */
function nameToBrickTypeCode(name) {
  return String(name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'delivered') return 'brick-status--ok';
  if (s === 'cancelled') return 'brick-status--muted';
  return 'brick-status--pending';
}

function tabActive(mode, pageKey) {
  if (pageKey === 'brick-catalog') return mode === 'catalog';
  if (pageKey === 'brick-production') return mode === 'production';
  if (pageKey === 'brick-deliveries') return mode === 'deliveries';
  return false;
}

const BrickFactoryPage = ({ mode = 'hub', onNavigate }) => {
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [summary, setSummary] = useState(null);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [types, setTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchExporting, setBatchExporting] = useState(false);

  const [typeCodeAuto, setTypeCodeAuto] = useState(true);
  const [typeRecipeAuto, setTypeRecipeAuto] = useState(true);
  const [tonnesPerLorryConfig, setTonnesPerLorryConfig] = useState(7);
  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    sizeLabel: '',
    defaultSellingPrice: '',
    bricksPerCementBag: '',
    sandUnit: 'tonne',
    sandPerCementBag: '',
    sandPer1000Bricks: '',
  });

  const [rawMaterials, setRawMaterials] = useState([]);
  const [markReadyQty, setMarkReadyQty] = useState({});

  const [prodForm, setProdForm] = useState({
    brickTypeId: '',
    productionDate: todayKey(),
    quantityProduced: '',
    quantityToReady: '0',
    quantityToCuring: '',
    cementBags: '',
    cementProductId: '',
    materialCostTzs: '',
    laborCostTzs: '',
    notes: '',
  });

  const [deliveryForm, setDeliveryForm] = useState({
    deliveryDate: todayKey(),
    customerName: '',
    customerPhone: '',
    destination: '',
    brickTypeId: '',
    quantity: '',
    unitPriceTzs: '',
    status: 'pending',
    deductStock: true,
  });

  const loadTypes = useCallback(async () => {
    const data = await apiJson('/brick/types');
    setTypes(data.types || []);
    return data.types || [];
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const tasks = [];
      if (mode === 'hub') {
        tasks.push(apiJson('/brick/summary').then(setSummary));
      }
      tasks.push(loadTypes());
      if (mode === 'production') {
        tasks.push(
          apiJson('/brick/production-context').then((d) => {
            setBatches([]);
            setRawMaterials(d.rawMaterials || []);
            if (d.types?.length) setTypes(d.types);
            if (d.tonnesPerLorry != null) setTonnesPerLorryConfig(d.tonnesPerLorry);
            return apiJson('/brick/production');
          }).then((d) => setBatches(d.batches || []))
        );
      }
      if (mode === 'deliveries') {
        tasks.push(apiJson('/brick/deliveries').then((d) => setDeliveries(d.deliveries || [])));
      }
      await Promise.all(tasks);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Failed to load brick factory data.' });
    } finally {
      setLoading(false);
    }
  }, [loadTypes, mode]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const activeTypes = useMemo(() => types.filter((t) => t.isActive !== false), [types]);

  const cementMaterials = useMemo(
    () =>
      rawMaterials.filter((p) => {
        const c = `${p.category} ${p.name}`.toLowerCase();
        if (c.includes('mchanga') || (c.includes('sand') && !c.includes('cement'))) return false;
        return c.includes('cement') || c.includes('simenti');
      }),
    [rawMaterials]
  );

  const sandMaterials = useMemo(
    () =>
      rawMaterials.filter((p) => {
        const c = `${p.category} ${p.name}`.toLowerCase();
        if (c.includes('cement') || c.includes('simenti')) return false;
        return c.includes('sand') || c.includes('mchanga') || c.includes('aggregat');
      }),
    [rawMaterials]
  );

  const selectedType = useMemo(
    () => activeTypes.find((t) => String(t.id) === String(prodForm.brickTypeId)),
    [activeTypes, prodForm.brickTypeId]
  );

  const netGoodPieces = useMemo(() => {
    return Math.max(0, Number(prodForm.quantityProduced) || 0);
  }, [prodForm.quantityProduced]);

  const cementBagsUsed = Number(prodForm.cementBags) || 0;

  const recipeSandPerBag = useMemo(
    () => effectiveSandPerCementBag(selectedType),
    [selectedType]
  );

  const expectedBricksFromRecipe = useMemo(() => {
    if (!selectedType?.bricksPerCementBag || cementBagsUsed <= 0) return null;
    return calcExpectedBricksFromCementBags(cementBagsUsed, selectedType.bricksPerCementBag);
  }, [selectedType, cementBagsUsed]);

  const brickVariance = useMemo(() => {
    if (expectedBricksFromRecipe == null || netGoodPieces <= 0) return null;
    return netGoodPieces - expectedBricksFromRecipe;
  }, [expectedBricksFromRecipe, netGoodPieces]);

  const selectedCementProduct = useMemo(
    () =>
      cementMaterials.find((p) => String(p.id) === String(prodForm.cementProductId)) ?? null,
    [cementMaterials, prodForm.cementProductId]
  );

  const selectedSandProduct = useMemo(
    () =>
      resolveMaterialProduct(rawMaterials, sandMaterials, null, selectedType?.sandProductId),
    [rawMaterials, sandMaterials, selectedType]
  );

  const cementStockError = useMemo(
    () => validateStockUsage(prodForm.cementBags, selectedCementProduct, 'Cement bags'),
    [prodForm.cementBags, selectedCementProduct]
  );

  const tPerLorry = tonnesPerLorry(tonnesPerLorryConfig);

  const autoSandTonnes = useMemo(() => {
    if (cementBagsUsed <= 0 || recipeSandPerBag <= 0) return 0;
    return calcSandTonnesFromCementBags(cementBagsUsed, recipeSandPerBag);
  }, [cementBagsUsed, recipeSandPerBag]);

  const sandStockError = useMemo(
    () => validateSandTonnesUsage(autoSandTonnes, selectedSandProduct, tPerLorry),
    [autoSandTonnes, selectedSandProduct, tPerLorry]
  );

  const sandAfterBatchLine = useMemo(() => {
    if (!selectedSandProduct || autoSandTonnes <= 0) return null;
    const available = stockQuantityToTonnes(
      selectedSandProduct.quantity,
      selectedSandProduct.unit,
      tPerLorry
    );
    const remaining = Math.max(0, available - autoSandTonnes);
    return formatSandUsageLine(remaining, tPerLorry);
  }, [selectedSandProduct, autoSandTonnes, tPerLorry]);

  const materialStockError = cementStockError || sandStockError;

  const productionStockTotals = useMemo(() => {
    let ready = 0;
    let curing = 0;
    let typesWithReady = 0;
    for (const t of activeTypes) {
      const r = Number(t.stockReady ?? t.stockOnHand) || 0;
      const c = Number(t.stockCuring) || 0;
      ready += r;
      curing += c;
      if (r > 0) typesWithReady += 1;
    }
    return { ready, curing, typesWithReady };
  }, [activeTypes]);

  const batchToReady = Number(prodForm.quantityToReady) || 0;
  const batchToCuring = Number(prodForm.quantityToCuring) || 0;
  const batchAllocationTotal = batchToReady + batchToCuring;
  const batchAllocationMismatch =
    netGoodPieces > 0 && batchAllocationTotal !== netGoodPieces;

  const projectedReadyStock = useMemo(() => {
    if (!selectedType) return null;
    const current = Number(selectedType.stockReady ?? selectedType.stockOnHand) || 0;
    return current + batchToReady;
  }, [selectedType, batchToReady]);

  const projectedCuringStock = useMemo(() => {
    if (!selectedType) return null;
    return (Number(selectedType.stockCuring) || 0) + batchToCuring;
  }, [selectedType, batchToCuring]);

  useEffect(() => {
    if (!cementMaterials.length) {
      if (prodForm.cementProductId) {
        setProdForm((f) => ({ ...f, cementProductId: '' }));
      }
      return;
    }
    const currentOk = cementMaterials.some(
      (p) => String(p.id) === String(prodForm.cementProductId)
    );
    if (currentOk) return;

    const linked = selectedType?.cementProductId
      ? cementMaterials.find((p) => String(p.id) === String(selectedType.cementProductId))
      : null;
    const pick = linked ?? cementMaterials[0];
    setProdForm((f) => ({ ...f, cementProductId: String(pick.id) }));
  }, [cementMaterials, selectedType?.cementProductId, prodForm.cementProductId]);

  useEffect(() => {
    if (!prodForm.brickTypeId && activeTypes.length) {
      setProdForm((f) => ({ ...f, brickTypeId: String(activeTypes[0].id) }));
    }
    if (!deliveryForm.brickTypeId && activeTypes.length) {
      const t = activeTypes[0];
      setDeliveryForm((f) => ({
        ...f,
        brickTypeId: String(t.id),
        unitPriceTzs: t.defaultSellingPrice != null ? String(t.defaultSellingPrice) : '',
      }));
    }
  }, [activeTypes, prodForm.brickTypeId, deliveryForm.brickTypeId]);

  const editingType = useMemo(
    () => types.find((t) => String(t.id) === String(editingTypeId)) || null,
    [types, editingTypeId]
  );

  const openEditType = (type) => {
    if (!isOwner) return;
    setEditingTypeId(type.id);
    setEditForm(typeToEditForm(type));
  };

  const closeEditType = () => {
    setEditingTypeId(null);
    setEditForm(null);
  };

  const handleSaveEditType = async (e) => {
    e.preventDefault();
    if (!isOwner || !editingTypeId || !editForm) return;

    const payload = {
      name: editForm.name.trim(),
      code: editForm.code.trim() || null,
      sizeLabel: editForm.sizeLabel.trim() || null,
    };
    const duplicate = findDuplicateBrickType(
      types.filter((t) => String(t.id) !== String(editingTypeId)),
      payload
    );
    if (duplicate) {
      setMsg({
        type: 'error',
        text: `This brick type already exists in your catalog ("${duplicate.name}"). Please review the existing entry or edit it instead of adding a duplicate.`,
      });
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch(`/brick/types/${editingTypeId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...payload,
          defaultSellingPrice: editForm.defaultSellingPrice
            ? Number(editForm.defaultSellingPrice)
            : null,
          bricksPerCementBag: editForm.bricksPerCementBag
            ? Number(editForm.bricksPerCementBag)
            : null,
          sandUnit: editForm.sandUnit,
          sandPerCementBag: editForm.sandPerCementBag ? Number(editForm.sandPerCementBag) : null,
          sandPer1000Bricks: editForm.sandPer1000Bricks ? Number(editForm.sandPer1000Bricks) : null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not update brick type.');
      }
      closeEditType();
      await loadTypes();
      setMsg({ type: 'success', text: 'Brick type updated.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteType = async (type) => {
    if (!isOwner) return;
    const label = type.name || 'this brick type';
    if (
      !window.confirm(
        `Delete "${label}"?\n\nThis cannot be undone. Types with stock, production, deliveries, or sales cannot be deleted.`
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch(`/brick/types/${type.id}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not delete brick type.');
      }
      if (String(editingTypeId) === String(type.id)) {
        closeEditType();
      }
      await loadTypes();
      setMsg({ type: 'success', text: body.message || 'Brick type deleted.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Delete failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    const payload = {
      name: typeForm.name.trim(),
      code: typeForm.code.trim() || null,
      sizeLabel: typeForm.sizeLabel.trim() || null,
    };
    const duplicate = findDuplicateBrickType(types, payload);
    if (duplicate) {
      setMsg({
        type: 'error',
        text: `This brick type already exists in your catalog ("${duplicate.name}"). Please review the existing entry or edit it instead of adding a duplicate.`,
      });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch('/brick/types', {
        method: 'POST',
        body: JSON.stringify({
          ...payload,
          defaultSellingPrice: typeForm.defaultSellingPrice ? Number(typeForm.defaultSellingPrice) : null,
          bricksPerCementBag: typeForm.bricksPerCementBag ? Number(typeForm.bricksPerCementBag) : null,
          sandUnit: typeForm.sandUnit,
          sandPerCementBag: typeForm.sandPerCementBag ? Number(typeForm.sandPerCementBag) : null,
          sandPer1000Bricks: typeForm.sandPer1000Bricks ? Number(typeForm.sandPer1000Bricks) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not save brick type.');
      }
      setTypeForm({
        name: '',
        code: '',
        sizeLabel: '',
        defaultSellingPrice: '',
        bricksPerCementBag: '',
        sandUnit: 'tonne',
        sandPerCementBag: '',
        sandPer1000Bricks: '',
      });
      setTypeCodeAuto(true);
      setTypeRecipeAuto(true);
      await loadTypes();
      setMsg({ type: 'success', text: 'Brick type saved.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const syncCuringFromNet = (produced, ready) => {
    const net = Math.max(0, Number(produced) || 0);
    const r = Number(ready) || 0;
    return String(Math.max(0, net - r));
  };

  const handleProduction = async (e) => {
    e.preventDefault();
    if (!prodForm.cementBags || Number(prodForm.cementBags) <= 0) {
      setMsg({ type: 'error', text: 'Enter cement bags used.' });
      return;
    }
    if (cementMaterials.length && !prodForm.cementProductId) {
      setMsg({ type: 'error', text: 'Select cement from stock.' });
      return;
    }
    if (materialStockError) {
      setMsg({ type: 'error', text: materialStockError });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const toReady = Number(prodForm.quantityToReady) || 0;
      const toCuring =
        prodForm.quantityToCuring !== ''
          ? Number(prodForm.quantityToCuring)
          : Math.max(0, netGoodPieces - toReady);

      const res = await apiFetch('/brick/production', {
        method: 'POST',
        body: JSON.stringify({
          brickTypeId: Number(prodForm.brickTypeId),
          productionDate: prodForm.productionDate,
          quantityProduced: Number(prodForm.quantityProduced),
          quantityWastage: 0,
          quantityDamaged: 0,
          quantityToCuring: toCuring,
          quantityToReady: toReady,
          cementBags: prodForm.cementBags ? Number(prodForm.cementBags) : null,
          cementProductId: prodForm.cementProductId ? Number(prodForm.cementProductId) : null,
          sandProductId: selectedSandProduct?.id ? Number(selectedSandProduct.id) : null,
          materialCostTzs: prodForm.materialCostTzs ? Number(prodForm.materialCostTzs) : null,
          laborCostTzs: prodForm.laborCostTzs ? Number(prodForm.laborCostTzs) : null,
          notes: prodForm.notes.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Could not record production.');
      }
      let successText = 'Production saved.';
      const d = body.materialDeductions;
      if (d?.cement?.deducted != null) {
        successText += ` Cement stock −${d.cement.deducted} ${d.cement.unit || 'mifuko'}.`;
      }
      if (d?.sand?.tonnesUsed != null) {
        successText += ` Mchanga stock −${d.sand.tonnesUsed} tone`;
        if (d.sand.stockDeducted != null && d.sand.stockUnit) {
          successText += ` (${d.sand.stockDeducted} ${d.sand.stockUnit})`;
        }
        successText += '.';
      }
      setProdForm((f) => ({
        ...f,
        quantityProduced: '',
        quantityToReady: '0',
        quantityToCuring: '',
        cementBags: '',
        notes: '',
      }));
      const [batchData, ctx] = await Promise.all([
        apiJson('/brick/production'),
        apiJson('/brick/production-context'),
      ]);
      setBatches(batchData.batches || []);
      setTypes(ctx.types || []);
      setRawMaterials(ctx.rawMaterials || []);
      if (ctx.tonnesPerLorry != null) setTonnesPerLorryConfig(ctx.tonnesPerLorry);
      setSummary(await apiJson('/brick/summary'));
      setMsg({ type: 'success', text: successText });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const productionBatches = useMemo(
    () => batches.filter((b) => !isDamageBatch(b)),
    [batches]
  );

  const productionExportRows = useMemo(
    () => productionBatches.map((b) => productionExportRow(b)),
    [productionBatches]
  );

  const exportBranding = useMemo(() => brandingFromUser(user), [user]);

  const handleDownloadProductionCsv = useCallback(() => {
    if (!productionBatches.length) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const factoryName = tenantDisplayName(user);
    downloadCsv(
      `production-batches_${stamp}.csv`,
      [PRODUCTION_EXPORT_HEAD, ...productionExportRows],
      {
        branding: exportBranding,
        title: `Production batches (${productionBatches.length})`,
        subtitle: `${factoryName} — uzalishaji wa matofali`,
      }
    );
  }, [productionBatches.length, productionExportRows, exportBranding, user]);

  const handleDownloadProductionPdf = useCallback(async () => {
    if (!productionBatches.length || batchExporting) return;
    setBatchExporting(true);
    setMsg(null);
    try {
      const factoryName = tenantDisplayName(user);
      await exportTableToPdf(
        `Production batches (${productionBatches.length})`,
        PRODUCTION_EXPORT_HEAD,
        productionExportRows,
        {
          branding: exportBranding,
          theme: 'factory-production',
          subtitle: `${factoryName} — uzalishaji wa matofali`,
          criteria: [
            `Exported: ${new Date().toLocaleString('en-GB')}`,
            `Records: ${productionBatches.length}`,
          ],
        }
      );
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not create PDF. Try again.' });
    } finally {
      setBatchExporting(false);
    }
  }, [productionBatches.length, batchExporting, productionExportRows, exportBranding, user]);

  const handleMarkReady = async (typeId) => {
    const qty = Number(markReadyQty[typeId]);
    if (!qty || qty < 1) {
      setMsg({ type: 'error', text: 'Enter how many bricks to move from curing to ready.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const data = await apiFetch(`/brick/types/${typeId}/mark-ready`, {
        method: 'POST',
        body: JSON.stringify({ quantity: qty }),
      }).then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || 'Could not update stock.');
        return json;
      });
      setMarkReadyQty((m) => ({ ...m, [typeId]: '' }));
      const ctx = await apiJson('/brick/production-context');
      setTypes(ctx.types || []);
      setSummary(await apiJson('/brick/summary'));
      setMsg({
        type: 'success',
        text: `Moved ${data.moved} pcs to ready for sale (POS).`,
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDelivery = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const type = activeTypes.find((t) => String(t.id) === String(deliveryForm.brickTypeId));
      const res = await apiFetch('/brick/deliveries', {
        method: 'POST',
        body: JSON.stringify({
          deliveryDate: deliveryForm.deliveryDate,
          customerName: deliveryForm.customerName.trim(),
          customerPhone: deliveryForm.customerPhone.trim() || null,
          destination: deliveryForm.destination.trim() || null,
          status: deliveryForm.status,
          deductStock: !!deliveryForm.deductStock,
          items: [
            {
              brickTypeId: Number(deliveryForm.brickTypeId),
              quantity: Number(deliveryForm.quantity),
              unitPriceTzs: deliveryForm.unitPriceTzs
                ? Number(deliveryForm.unitPriceTzs)
                : type?.defaultSellingPrice ?? null,
            },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not save delivery.');
      }
      setDeliveryForm((f) => ({
        ...f,
        customerName: '',
        customerPhone: '',
        destination: '',
        quantity: '',
      }));
      const data = await apiJson('/brick/deliveries');
      setDeliveries(data.deliveries || []);
      await loadTypes();
      setSummary(await apiJson('/brick/summary'));
      setMsg({ type: 'success', text: 'Delivery recorded.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const updateDeliveryStatus = async (id, status) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch(`/brick/deliveries/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Status update failed.');
      const data = await apiJson('/brick/deliveries');
      setDeliveries(data.deliveries || []);
      setMsg({ type: 'success', text: `Delivery marked ${status}.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Update failed.' });
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === 'catalog'
      ? 'Brick & tile types'
      : mode === 'production'
        ? 'Production'
        : mode === 'deliveries'
          ? 'Deliveries to customers'
          : 'Production';

  const productionNavTabs = [
    { key: 'brick-catalog', label: 'Types' },
    { key: 'brick-production', label: 'Batches' },
  ];

  const salesNavTabs = [
    { key: 'pos-sale-products', label: 'POS sale' },
    { key: 'brick-deliveries', label: 'Deliveries' },
    { key: 'pos-sale-history', label: 'Sale history' },
  ];

  const navTabs = mode === 'deliveries' ? salesNavTabs : productionNavTabs;

  return (
    <div className="page-bleed brick-page">
      <div className="brick-page__head">
        <h1 className="brick-page__title">{title}</h1>
        <p className="brick-page__lead">
          {mode === 'production'
            ? 'Record bricks produced, cement and sand used — raw material stock decreases automatically. Curing bricks are not sold until you mark them ready.'
        : mode === 'deliveries'
          ? 'Dispatch bricks to customer sites. Sales from POS with “Deliver to site” appear here automatically (stock already deducted at sale).'
              : 'Plant output: catalog types and production batches. Customer deliveries and POS sales are under Sales (POS).'}
        </p>
      </div>

      <nav
        className="brick-tabs"
        aria-label={mode === 'deliveries' ? 'Sales sections' : 'Production sections'}
      >
        {navTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`brick-tabs__btn${tabActive(mode, t.key) ? ' brick-tabs__btn--active' : ''}`}
            onClick={() => onNavigate(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {msg ? (
        <div className={`brick-msg brick-msg--${msg.type}`} role="status">
          {msg.text}
        </div>
      ) : null}

      {loading && mode === 'hub' ? <p className="brick-loading">Loading…</p> : null}

      {mode === 'hub' && summary ? (
        <>
          <div className="brick-kpi-grid">
            <div className="brick-kpi">
              <span className="brick-kpi__label">Active brick types</span>
              <strong>{summary.brickTypes ?? 0}</strong>
            </div>
            <div className="brick-kpi">
              <span className="brick-kpi__label">Finished stock (pieces)</span>
              <strong>{formatTzs(summary.finishedStockPieces)}</strong>
            </div>
            <div className="brick-kpi">
              <span className="brick-kpi__label">Produced this month</span>
              <strong>{formatTzs(summary.producedThisMonth)}</strong>
            </div>
            <div className="brick-kpi">
              <span className="brick-kpi__label">Wastage this month</span>
              <strong>{formatTzs(summary.wastageThisMonth)}</strong>
            </div>
            <div className="brick-kpi">
              <span className="brick-kpi__label">Pending deliveries</span>
              <strong>{summary.pendingDeliveries ?? 0}</strong>
            </div>
          </div>

          <section className="brick-card">
            <h2>Workflow</h2>
            <ol className="brick-workflow">
              <li>
                <strong>Material orders</strong> — cement, sand, fuel from suppliers.
              </li>
              <li>
                <strong>Production batches</strong> — record output; good pieces add to finished stock.
              </li>
              <li>
                <strong>Raw materials</strong> — stock levels for inputs; types can link to POS.
              </li>
              <li>
                <strong>Sales (POS)</strong> — counter sales and deliveries to customers.
              </li>
              <li>
                <strong>Expenses</strong> — track plant costs and payments.
              </li>
            </ol>
            <div className="brick-quick">
              <button type="button" onClick={() => onNavigate('brick-catalog')}>
                Manage types
              </button>
              <button type="button" onClick={() => onNavigate('brick-production')}>
                Record production
              </button>
              <button type="button" onClick={() => onNavigate('brick-deliveries')}>
                New delivery
              </button>
              <button type="button" onClick={() => onNavigate('orders-make')}>
                Raw material order
              </button>
              <button type="button" onClick={() => onNavigate('pos-sale-products')}>
                POS sale
              </button>
            </div>
          </section>
        </>
      ) : null}

      {mode === 'catalog' ? (
        <div className="brick-split brick-split--catalog">
          <section className="brick-card brick-card--form-col">
            <h2>Add brick type</h2>
            <form className="brick-form" onSubmit={handleAddType}>
              <label>
                Name
                <input
                  required
                  value={typeForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setTypeForm((f) => ({
                      ...f,
                      name,
                      code: typeCodeAuto ? nameToBrickTypeCode(name) : f.code,
                    }));
                  }}
                  placeholder="e.g. Solid 6 inch"
                />
              </label>
              <label>
                Code
                <input
                  value={typeForm.code}
                  onChange={(e) => {
                    setTypeCodeAuto(false);
                    setTypeForm((f) => ({ ...f, code: e.target.value }));
                  }}
                  placeholder={typeCodeAuto ? 'Auto from name' : 'SKU / code'}
                />
              </label>
              <label>
                Size
                <BrickSizeField
                  value={typeForm.sizeLabel}
                  onChange={(sizeLabel) => {
                    const defaults = recipeDefaultsForBrickSize(sizeLabel);
                    setTypeForm((f) => {
                      if (!typeRecipeAuto || !defaults) {
                        return { ...f, sizeLabel };
                      }
                      return {
                        ...f,
                        sizeLabel,
                        sandUnit: defaults.sandUnit,
                        sandPerCementBag: String(defaults.sandPerCementBag),
                        sandPer1000Bricks: String(defaults.sandPer1000Bricks),
                        bricksPerCementBag: String(defaults.bricksPerCementBag),
                      };
                    });
                  }}
                  disabled={busy}
                />
              </label>
              <label>
                Default price (TZS)
                <input
                  type="number"
                  min="0"
                  value={typeForm.defaultSellingPrice}
                  onChange={(e) => setTypeForm((f) => ({ ...f, defaultSellingPrice: e.target.value }))}
                />
              </label>
              <fieldset className="brick-recipe-fieldset">
                <legend>Recipe kwa mifuko 1 ya cement</legend>
                <label>
                  Matofali kwa mifuko 1 ya cement
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={typeForm.bricksPerCementBag}
                    onChange={(e) => {
                      setTypeRecipeAuto(false);
                      const bricksPerCementBag = e.target.value;
                      setTypeForm((f) => {
                        const next = { ...f, bricksPerCementBag };
                        if (f.sandPerCementBag) {
                          next.sandPer1000Bricks = calcSandPer1000FromPerBag(
                            f.sandPerCementBag,
                            bricksPerCementBag
                          );
                        } else if (f.sandPer1000Bricks) {
                          next.sandPerCementBag = calcSandPerCementBagFromPer1000(
                            f.sandPer1000Bricks,
                            bricksPerCementBag
                          );
                        }
                        return next;
                      });
                    }}
                    placeholder="mf. 400"
                  />
                </label>
                <label>
                  Mchanga kwa mifuko 1 ya cement (tone)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={typeForm.sandPerCementBag}
                    onChange={(e) => {
                      setTypeRecipeAuto(false);
                      const sandPerCementBag = e.target.value;
                      setTypeForm((f) => ({
                        ...f,
                        sandPerCementBag,
                        sandUnit: 'tonne',
                        sandPer1000Bricks: calcSandPer1000FromPerBag(
                          sandPerCementBag,
                          f.bricksPerCementBag
                        ),
                      }));
                    }}
                    placeholder="mf. 0.48"
                  />
                </label>
              </fieldset>
              <button type="submit" className="brick-btn brick-btn--primary" disabled={busy}>
                Save type
              </button>
            </form>
          </section>

          <section className="brick-card brick-card--grow brick-card--catalog-table">
            <h2 className="brick-catalog-title">Brick type catalog</h2>
            {loading ? <p className="brick-loading">Loading…</p> : null}
            {!loading && !types.length ? (
              <p className="brick-empty">No brick types yet. Add your first type.</p>
            ) : null}
            {types.length ? (
              <div className="brick-table-wrap">
                <table className="brick-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Matofali / mfuko 1</th>
                      <th className="brick-table__col-price">{isOwner ? 'Price (TZS)' : 'Price'}</th>
                      <th>Date</th>
                      {isOwner ? <th aria-label="Actions" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {types.map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{t.sizeLabel || '—'}</td>
                        <td>
                          {t.bricksPerCementBag != null && Number(t.bricksPerCementBag) > 0
                            ? Number(t.bricksPerCementBag).toLocaleString('en-TZ', {
                                maximumFractionDigits: 0,
                              })
                            : '—'}
                        </td>
                        <td className="brick-table__col-price">{formatTzs(t.defaultSellingPrice)}</td>
                        <td className="brick-table__date">{formatCatalogDate(t)}</td>
                        {isOwner ? (
                          <td className="brick-table__actions">
                            <div className="brick-table__action-group">
                              <button
                                type="button"
                                className="brick-btn brick-btn--edit brick-btn--sm"
                                disabled={busy}
                                onClick={() => openEditType(t)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="brick-btn brick-btn--danger brick-btn--sm"
                                disabled={busy}
                                onClick={() => void handleDeleteType(t)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          {isOwner && editingType && editForm ? (
            <div
              className="brick-catalog-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="brick-edit-title"
              onClick={closeEditType}
            >
              <div
                className="brick-catalog-modal__panel"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="brick-catalog-modal__header">
                  <h3 id="brick-edit-title">Edit brick type</h3>
                  <p className="brick-catalog-modal__sub">{editingType.name}</p>
                  <button
                    type="button"
                    className="brick-catalog-modal__close"
                    onClick={closeEditType}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </header>
                <form className="brick-form brick-catalog-modal__form" onSubmit={handleSaveEditType}>
                  <label>
                    Name
                    <input
                      required
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </label>
                  <label>
                    Code
                    <input
                      value={editForm.code}
                      onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
                    />
                  </label>
                  <label>
                    Size
                    <BrickSizeField
                      value={editForm.sizeLabel}
                      onChange={(sizeLabel) => setEditForm((f) => ({ ...f, sizeLabel }))}
                      disabled={busy}
                    />
                  </label>
                  <label>
                    Price (TZS)
                    <input
                      type="number"
                      min="0"
                      value={editForm.defaultSellingPrice}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, defaultSellingPrice: e.target.value }))
                      }
                    />
                  </label>
                  <fieldset className="brick-recipe-fieldset">
                    <legend>Recipe kwa mifuko 1 ya cement</legend>
                    <label>
                      Matofali kwa mifuko 1 ya cement
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={editForm.bricksPerCementBag}
                        onChange={(e) => {
                          const bricksPerCementBag = e.target.value;
                          setEditForm((f) => {
                            const next = { ...f, bricksPerCementBag };
                            if (f.sandPerCementBag) {
                              next.sandPer1000Bricks = calcSandPer1000FromPerBag(
                                f.sandPerCementBag,
                                bricksPerCementBag
                              );
                            }
                            return next;
                          });
                        }}
                      />
                    </label>
                    <label>
                      Mchanga kwa mifuko 1 ya cement (tone)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.sandPerCementBag}
                        onChange={(e) => {
                          const sandPerCementBag = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            sandPerCementBag,
                            sandUnit: 'tonne',
                            sandPer1000Bricks: calcSandPer1000FromPerBag(
                              sandPerCementBag,
                              f.bricksPerCementBag
                            ),
                          }));
                        }}
                      />
                    </label>
                  </fieldset>
                  <div className="brick-catalog-modal__actions">
                    <button
                      type="button"
                      className="brick-btn brick-btn--secondary"
                      disabled={busy}
                      onClick={closeEditType}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="brick-btn brick-btn--primary" disabled={busy}>
                      Save changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'production' ? (
        <>
          <section className="brick-card brick-prod-stock brick-prod-stock--panel">
            <header className="brick-ready-hero">
              <div>
                <h2 className="brick-ready-hero__title">Matofali tayari kuuza</h2>
                <p className="brick-ready-hero__sub">
                  Tayari = POS na delivery · Curing = bado maji — hamisha kwenye kadi chini ya aina husika.
                </p>
              </div>
            </header>
            {!activeTypes.length ? (
              <p className="brick-empty">
                Add a brick type first.{' '}
                <button type="button" className="brick-link" onClick={() => onNavigate('brick-catalog')}>
                  Catalog
                </button>
              </p>
            ) : (
              <>
                <div className="brick-ready-stats">
                  <article className="brick-ready-stats__item brick-ready-stats__item--ready">
                    <span className="brick-ready-stats__icon" aria-hidden>
                      <LuShoppingBag size={18} />
                    </span>
                    <div>
                      <span className="brick-ready-stats__label">Ready (POS)</span>
                      <strong className="brick-ready-stats__value">
                        {formatPcs(productionStockTotals.ready)}
                        <span className="brick-ready-stats__unit"> pcs</span>
                      </strong>
                    </div>
                  </article>
                  <article className="brick-ready-stats__item brick-ready-stats__item--curing">
                    <span className="brick-ready-stats__icon" aria-hidden>
                      <LuDroplets size={18} />
                    </span>
                    <div>
                      <span className="brick-ready-stats__label">Curing (water)</span>
                      <strong className="brick-ready-stats__value">
                        {formatPcs(productionStockTotals.curing)}
                        <span className="brick-ready-stats__unit"> pcs</span>
                      </strong>
                    </div>
                  </article>
                  <article className="brick-ready-stats__item brick-ready-stats__item--types">
                    <span className="brick-ready-stats__icon" aria-hidden>
                      <LuPackage size={18} />
                    </span>
                    <div>
                      <span className="brick-ready-stats__label">Aina zenye stock</span>
                      <strong className="brick-ready-stats__value">
                        {productionStockTotals.typesWithReady}
                        <span className="brick-ready-stats__unit"> / {activeTypes.length}</span>
                      </strong>
                    </div>
                  </article>
                </div>
                <div className="brick-prod-stock__grid">
                  {activeTypes.map((t) => {
                    const ready = Number(t.stockReady ?? t.stockOnHand) || 0;
                    const curing = Number(t.stockCuring) || 0;
                    const total = ready + curing;
                    const readyPct = total > 0 ? Math.round((ready / total) * 100) : ready > 0 ? 100 : 0;
                    return (
                      <article
                        key={t.id}
                        className={`brick-prod-stock__card${ready > 0 ? ' brick-prod-stock__card--has-ready' : ''}`}
                      >
                        <div className="brick-prod-stock__card-head">
                          <h3>{t.name}</h3>
                          {t.sizeLabel ? (
                            <span className="brick-prod-stock__size">{t.sizeLabel}</span>
                          ) : null}
                        </div>
                        <div className="brick-prod-stock__meter" aria-hidden>
                          <span
                            className="brick-prod-stock__meter-ready"
                            style={{ width: `${readyPct}%` }}
                          />
                        </div>
                        <div className="brick-prod-stock__counts brick-prod-stock__counts--split">
                          <div className="brick-prod-stock__pill brick-prod-stock__pill--ready">
                            <span>
                              <LuShoppingBag size={14} aria-hidden /> Tayari kuuza
                            </span>
                            <strong>{formatPcs(ready)}</strong>
                          </div>
                          <div className="brick-prod-stock__pill brick-prod-stock__pill--curing">
                            <span>
                              <LuDroplets size={14} aria-hidden /> Curing
                            </span>
                            <strong>{formatPcs(curing)}</strong>
                          </div>
                        </div>
                        {curing > 0 ? (
                          <div className="brick-prod-stock__move">
                            <label className="brick-prod-stock__move-label">
                              Hamisha curing → ready
                              <input
                                type="number"
                                min="1"
                                max={curing}
                                placeholder={`Max ${formatPcs(curing)}`}
                                value={markReadyQty[t.id] ?? ''}
                                onChange={(e) =>
                                  setMarkReadyQty((m) => ({ ...m, [t.id]: e.target.value }))
                                }
                              />
                            </label>
                            <button
                              type="button"
                              className="brick-btn brick-btn--secondary brick-prod-stock__move-btn"
                              disabled={busy}
                              onClick={() => handleMarkReady(t.id)}
                            >
                              <LuArrowRight size={16} aria-hidden /> Mark ready
                            </button>
                          </div>
                        ) : (
                          <p className="brick-prod-stock__idle">Hakuna curing — uzalishaji mpya uweke curing kwanza.</p>
                        )}
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          <section className="brick-card brick-card--form brick-card--production-form">
            <h2>Record production</h2>
            {!activeTypes.length ? (
              <p className="brick-empty">
                Add a brick type first.{' '}
                <button type="button" className="brick-link" onClick={() => onNavigate('brick-catalog')}>
                  Go to catalog
                </button>
              </p>
            ) : (
              <form className="brick-form brick-form--wide" onSubmit={handleProduction}>
                <label>
                  Brick type
                  <select
                    required
                    value={prodForm.brickTypeId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const t = activeTypes.find((x) => String(x.id) === id);
                      const linkedCement = t?.cementProductId
                        ? cementMaterials.find((p) => String(p.id) === String(t.cementProductId))
                        : null;
                      const cementProductId = linkedCement
                        ? String(linkedCement.id)
                        : cementMaterials[0]
                          ? String(cementMaterials[0].id)
                          : '';
                      setProdForm((f) => ({
                        ...f,
                        brickTypeId: id,
                        cementProductId,
                      }));
                    }}
                  >
                    {activeTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedType ? (
                  <div className="brick-ready-inline" role="status">
                    <span className="brick-ready-inline__chip brick-ready-inline__chip--ready">
                      <LuShoppingBag size={14} aria-hidden />
                      Tayari: <strong>{formatPcs(selectedType.stockReady ?? selectedType.stockOnHand)}</strong>
                    </span>
                    <span className="brick-ready-inline__chip brick-ready-inline__chip--curing">
                      <LuDroplets size={14} aria-hidden />
                      Curing: <strong>{formatPcs(selectedType.stockCuring)}</strong>
                    </span>
                  </div>
                ) : null}
                <label>
                  Production date
                  <input
                    type="date"
                    required
                    value={prodForm.productionDate}
                    onChange={(e) => setProdForm((f) => ({ ...f, productionDate: e.target.value }))}
                  />
                </label>
                <label>
                  Cement (from stock)
                  <select
                    required={cementMaterials.length > 0}
                    disabled={!cementMaterials.length || busy}
                    value={prodForm.cementProductId}
                    onChange={(e) => setProdForm((f) => ({ ...f, cementProductId: e.target.value }))}
                  >
                    {!cementMaterials.length ? (
                      <option value="">No cement in stock</option>
                    ) : (
                      cementMaterials.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {p.quantity} {p.unit || 'bags'}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                <label>
                  Bags used
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    required
                    max={
                      selectedCementProduct
                        ? Number(selectedCementProduct.quantity) || undefined
                        : undefined
                    }
                    className={cementStockError ? 'brick-input--error' : undefined}
                    value={prodForm.cementBags}
                    onChange={(e) => setProdForm((f) => ({ ...f, cementBags: e.target.value }))}
                  />
                </label>
                {cementStockError ? (
                  <p className="brick-prod-stock__hint brick-prod-stock__hint--warn" role="alert">
                    {cementStockError}
                  </p>
                ) : null}
                {expectedBricksFromRecipe != null && cementBagsUsed > 0 ? (
                  <div className="brick-recipe-expect" role="status">
                    <p className="brick-recipe-expect__title">
                      Tarajio: <strong>{expectedBricksFromRecipe}</strong> matofali
                      {autoSandTonnes > 0 ? (
                        <>
                          {' '}
                          · <strong>{formatSandUsageLine(autoSandTonnes, tPerLorry)}</strong> mchanga
                        </>
                      ) : null}
                    </p>
                  </div>
                ) : selectedType && (!selectedType.bricksPerCementBag || recipeSandPerBag <= 0) ? (
                  <p className="brick-prod-stock__hint brick-prod-stock__hint--warn">
                    Weka recipe kwenye{' '}
                    <button type="button" className="brick-link" onClick={() => onNavigate('brick-catalog')}>
                      Catalog
                    </button>
                    .
                  </p>
                ) : null}
                <label>
                  Good bricks produced (pieces)
                  <input
                    type="number"
                    min="1"
                    required
                    value={prodForm.quantityProduced}
                    onChange={(e) => {
                      const v = e.target.value;
                      setProdForm((f) => ({
                        ...f,
                        quantityProduced: v,
                        quantityToCuring: syncCuringFromNet(v, f.quantityToReady),
                      }));
                    }}
                  />
                </label>
                <p className="brick-prod-stock__hint">
                  Matofali yaliyovunjika — rekodi kwenye{' '}
                  <button type="button" className="brick-link" onClick={() => onNavigate('damaged-products')}>
                    Damaged stock
                  </button>
                  .
                </p>
                {brickVariance != null && expectedBricksFromRecipe != null ? (
                  <div
                    className={`brick-recipe-variance${
                      brickVariance === 0 ? ' brick-recipe-variance--ok' : ' brick-recipe-variance--warn'
                    }`}
                    role="status"
                  >
                    <p>
                      {brickVariance === 0 ? (
                        <>Inalingana na recipe.</>
                      ) : (
                        <>
                          Tofauti: <strong>{formatBrickVariance(brickVariance)}</strong> (tarajio{' '}
                          {expectedBricksFromRecipe}, halisi {netGoodPieces})
                        </>
                      )}
                    </p>
                  </div>
                ) : null}
                <div
                  className={`brick-ready-alloc${batchAllocationMismatch ? ' brick-ready-alloc--warn' : ''}`}
                  role="group"
                  aria-labelledby="brick-ready-alloc-title"
                >
                  <div className="brick-ready-alloc__head">
                    <h3 id="brick-ready-alloc-title" className="brick-ready-alloc__title">
                      Ugawaji wa batch hii
                    </h3>
                    {netGoodPieces > 0 ? (
                      <span className="brick-ready-alloc__net">
                        Net halisi: <strong>{formatPcs(netGoodPieces)}</strong> pcs
                      </span>
                    ) : null}
                  </div>
                  <div className="brick-ready-alloc__grid">
                    <label className="brick-ready-alloc__field brick-ready-alloc__field--ready">
                      <span className="brick-ready-alloc__label">
                        <LuShoppingBag size={15} aria-hidden /> Tayari kuuza (sasa)
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={netGoodPieces || undefined}
                        value={prodForm.quantityToReady}
                        onChange={(e) => {
                          const r = e.target.value;
                          setProdForm((f) => ({
                            ...f,
                            quantityToReady: r,
                            quantityToCuring: syncCuringFromNet(f.quantityProduced, r),
                          }));
                        }}
                      />
                      {projectedReadyStock != null && batchToReady > 0 ? (
                        <span className="brick-ready-alloc__after">
                          Baada: <strong>{formatPcs(projectedReadyStock)}</strong> tayari
                        </span>
                      ) : null}
                    </label>
                    <label className="brick-ready-alloc__field brick-ready-alloc__field--curing">
                      <span className="brick-ready-alloc__label">
                        <LuDroplets size={15} aria-hidden /> Curing (maji)
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={netGoodPieces || undefined}
                        value={prodForm.quantityToCuring}
                        onChange={(e) => setProdForm((f) => ({ ...f, quantityToCuring: e.target.value }))}
                      />
                      {projectedCuringStock != null && batchToCuring > 0 ? (
                        <span className="brick-ready-alloc__after">
                          Baada: <strong>{formatPcs(projectedCuringStock)}</strong> curing
                        </span>
                      ) : null}
                    </label>
                  </div>
                  {batchAllocationMismatch ? (
                    <p className="brick-prod-stock__hint brick-prod-stock__hint--warn" role="alert">
                      Tayari + curing = {formatPcs(batchAllocationTotal)} — lazima iwe {formatPcs(netGoodPieces)}{' '}
                      (net halisi).
                    </p>
                  ) : null}
                </div>
                <div
                  className={`brick-sand-summary${sandStockError ? ' brick-sand-summary--error' : ''}`}
                  role="status"
                >
                  <h3 className="brick-sand-summary__title">Sand (auto)</h3>
                  {selectedSandProduct ? (
                    <p className="brick-prod-stock__hint">
                      Stock:{' '}
                      <strong>{formatSandStockAvailability(selectedSandProduct, tPerLorry)}</strong>
                      {selectedSandProduct.name ? ` (${selectedSandProduct.name})` : ''}
                    </p>
                  ) : null}
                  {autoSandTonnes > 0 ? (
                    <p className="brick-sand-summary__amount">
                      Mahitaji batch: <strong>{formatSandUsageLine(autoSandTonnes, tPerLorry)}</strong>
                      {sandAfterBatchLine ? (
                        <span className="brick-muted"> → baada: {sandAfterBatchLine}</span>
                      ) : null}
                    </p>
                  ) : (
                    <p className="brick-prod-stock__hint brick-prod-stock__hint--warn">
                      Hakuna recipe.{' '}
                      <button type="button" className="brick-link" onClick={() => onNavigate('brick-catalog')}>
                        Catalog
                      </button>
                    </p>
                  )}
                  {sandStockError ? (
                    <p className="brick-prod-stock__hint brick-prod-stock__hint--warn" role="alert">
                      {sandStockError}
                    </p>
                  ) : null}
                </div>
                <label>
                  Notes
                  <textarea
                    rows={2}
                    value={prodForm.notes}
                    onChange={(e) => setProdForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </label>
                <button
                  type="submit"
                  className="brick-btn brick-btn--primary"
                  disabled={busy || Boolean(materialStockError)}
                >
                  Save production
                </button>
              </form>
            )}
          </section>

          <section className="brick-card brick-card--grow">
            <div className="brick-batch-export-head">
              <h2>Production batches</h2>
              {productionBatches.length ? (
                <div className="brick-batch-export-actions">
                  <button
                    type="button"
                    className="brick-btn brick-btn--secondary brick-btn--sm"
                    onClick={handleDownloadProductionCsv}
                    disabled={loading || batchExporting}
                    title="Download production CSV (key fields only)"
                  >
                    Download CSV
                  </button>
                  <button
                    type="button"
                    className="brick-btn brick-btn--secondary brick-btn--sm brick-batch-export-pdf"
                    onClick={() => void handleDownloadProductionPdf()}
                    disabled={loading || batchExporting}
                    title="Download production PDF"
                  >
                    {batchExporting ? 'PDF…' : 'Download PDF'}
                  </button>
                </div>
              ) : null}
            </div>
            {loading ? <p className="brick-loading">Loading…</p> : null}
            {!loading && !productionBatches.length ? (
              <p className="brick-empty">No production recorded yet.</p>
            ) : null}
            {productionBatches.length ? (
              <div className="brick-table-wrap">
                <table className="brick-table brick-table--production">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Brick type</th>
                      <th>Good bricks</th>
                      <th>Ready</th>
                      <th>Curing</th>
                      <th>Cement</th>
                      <th>Sand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionBatches.map((b) => (
                      <tr
                        key={b.id}
                        className={b.hasRecipeDiscrepancy ? 'brick-table__row--variance' : undefined}
                      >
                        <td>{b.productionDate}</td>
                        <td>{b.brickTypeName}</td>
                        <td>{formatPcs(b.netPieces ?? b.quantityProduced)}</td>
                        <td>{formatPcs(b.quantityToReady)}</td>
                        <td>{formatPcs(b.quantityToCuring)}</td>
                        <td>{b.cementBags ?? '—'}</td>
                        <td>{formatSandAmount(b.sandQuantity, b.sandUnit) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {mode === 'deliveries' ? (
        <div className="brick-split">
          <section className="brick-card">
            <h2>New delivery (manual)</h2>
            <p className="brick-prod-stock__hint">
              For counter sales with site delivery, use <strong>POS sale</strong> — they appear in the list automatically.
            </p>
            {!activeTypes.length ? (
              <p className="brick-empty">
                Add brick types first.{' '}
                <button type="button" className="brick-link" onClick={() => onNavigate('brick-catalog')}>
                  Catalog
                </button>
              </p>
            ) : (
              <form className="brick-form" onSubmit={handleDelivery}>
                <label>
                  Delivery date
                  <input
                    type="date"
                    required
                    value={deliveryForm.deliveryDate}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, deliveryDate: e.target.value }))}
                  />
                </label>
                <label>
                  Customer name
                  <input
                    required
                    value={deliveryForm.customerName}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, customerName: e.target.value }))}
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={deliveryForm.customerPhone}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, customerPhone: e.target.value }))}
                  />
                </label>
                <label>
                  Destination / site
                  <input
                    value={deliveryForm.destination}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, destination: e.target.value }))}
                  />
                </label>
                <label>
                  Brick type
                  <select
                    required
                    value={deliveryForm.brickTypeId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const t = activeTypes.find((x) => String(x.id) === id);
                      setDeliveryForm((f) => ({
                        ...f,
                        brickTypeId: id,
                        unitPriceTzs:
                          t?.defaultSellingPrice != null ? String(t.defaultSellingPrice) : f.unitPriceTzs,
                      }));
                    }}
                  >
                    {activeTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} (ready: {t.stockReady ?? t.stockOnHand ?? 0})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Quantity (pieces)
                  <input
                    type="number"
                    min="1"
                    required
                    value={deliveryForm.quantity}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, quantity: e.target.value }))}
                  />
                </label>
                <label>
                  Unit price (TZS)
                  <input
                    type="number"
                    min="0"
                    value={deliveryForm.unitPriceTzs}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, unitPriceTzs: e.target.value }))}
                  />
                </label>
                <label>
                  Status
                  <select
                    value={deliveryForm.status}
                    onChange={(e) => setDeliveryForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    {/* <option value="delivered">Delivered</option> */}
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '18px 0 0 0' }}>
                  <label className="brick-check" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 16 }}>
                    <input
                      type="checkbox"
                      checked={deliveryForm.deductStock}
                      onChange={(e) => setDeliveryForm((f) => ({ ...f, deductStock: e.target.checked }))}
                      style={{ width: 20, height: 20, accentColor: '#e85d04', marginRight: 6 }}
                    />
                    <span style={{ lineHeight: 1.2, fontWeight: 500, color: '#374151' }}>Deduct from finished stock</span>
                  </label>
                  <button type="submit" className="brick-btn brick-btn--primary" disabled={busy} style={{ flex: 1, marginLeft: 12, minWidth: 180 }}>
                    Save delivery
                  </button>
                </div>
              </form>
            )}
          </section>

          <section className="brick-card brick-card--grow">
            <h2>Delivery list</h2>
            <p className="brick-prod-stock__hint">
              POS site sales are tagged <strong>From sale</strong>. Mark <strong>Delivered</strong> when bricks reach the site.
            </p>
            {loading ? <p className="brick-loading">Loading…</p> : null}
            {!loading && !deliveries.length ? <p className="brick-empty">No deliveries yet.</p> : null}
            {deliveries.length ? (
              <div className="brick-table-wrap">
                <table className="brick-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((d) => (
                      <tr key={d.id}>
                        <td>{d.deliveryDate}</td>
                        <td>
                          {d.source === 'pos' || d.saleId ? (
                            <span className="brick-tag brick-tag--pos" title={`POS sale #${d.saleId}`}>
                              From sale #{d.saleId}
                            </span>
                          ) : (
                            <span className="brick-tag brick-tag--manual">Manual</span>
                          )}
                        </td>
                        <td>
                          <div>{d.customerName}</div>
                          {d.customerPhone ? <small className="brick-muted">{d.customerPhone}</small> : null}
                          {d.destination ? <small className="brick-muted">{d.destination}</small> : null}
                        </td>
                        <td>
                          {(d.items || []).map((i, idx) => (
                            <div key={idx}>
                              {i.brickTypeName}: {i.quantity}
                            </div>
                          ))}
                        </td>
                        <td>{formatTzs(d.totalTzs)}</td>
                        <td>
                          <span className={`brick-status ${statusClass(d.status)}`}>{d.status}</span>
                        </td>
                        <td className="brick-actions">
                          {d.status === 'pending' ? (
                            <>
                              <button
                                type="button"
                                className="brick-btn brick-btn--sm brick-btn--primary"
                                disabled={busy}
                                onClick={() => updateDeliveryStatus(d.id, 'delivered')}
                              >
                                Delivered
                              </button>
                              <button
                                type="button"
                                className="brick-btn brick-btn--sm brick-btn--ghost"
                                disabled={busy}
                                onClick={() => updateDeliveryStatus(d.id, 'cancelled')}
                              >
                                Cancel
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default BrickFactoryPage;
